import { DOC_SECTIONS, type DocItem, type DocSection } from "./index";

/**
 * Server-side view of the in-app documentation for the get_woods_help chat
 * tool. Admin-only pages are excluded here rather than per request: the tool
 * runs for every user, so nothing behind the Admin badge may reach the model.
 */

export type HelpDoc = {
  id: string;
  title: string;
  section: string;
  route: string;
  content: string;
};

export type HelpDocMatch = Omit<HelpDoc, "content"> & { excerpt: string };

export const documentationRoute = (docId?: string) =>
  docId ? `/?nav=documentation&doc=${encodeURIComponent(docId)}` : "/?nav=documentation";

const toHelpDoc = (section: DocSection, item: DocItem): HelpDoc => ({
  id: item.id,
  title: item.title,
  section: section.title,
  route: documentationRoute(item.id),
  content: item.content.trim(),
});

export const PUBLIC_HELP_DOCS: readonly HelpDoc[] = DOC_SECTIONS.flatMap((section) =>
  section.items
    .filter((item) => item.badge !== "Admin")
    .map((item) => toHelpDoc(section, item)),
);

export function findHelpDoc(id: string): HelpDoc | undefined {
  return PUBLIC_HELP_DOCS.find((doc) => doc.id === id);
}

export function listHelpDocs(): Omit<HelpDoc, "content">[] {
  return PUBLIC_HELP_DOCS.map(({ content: _content, ...doc }) => doc);
}

const STOP_WORDS = new Set([
  "the", "and", "for", "how", "can", "what", "does", "with", "you", "your",
  "are", "use", "using", "into", "from", "this", "that", "woods", "about",
]);

const queryTerms = (query: string) =>
  [...new Set(
    query
      .toLocaleLowerCase()
      .split(/[^\p{L}\p{N}-]+/u)
      .map((term) => term.replace(/^-+|-+$/g, ""))
      .filter((term) => term.length > 2 && !STOP_WORDS.has(term)),
  )];

const countOccurrences = (haystack: string, needle: string) => {
  let count = 0;
  for (let i = haystack.indexOf(needle); i !== -1; i = haystack.indexOf(needle, i + needle.length)) {
    count += 1;
  }
  return count;
};

/** The paragraph-ish window around the first matched term. */
function excerptFor(content: string, terms: string[]): string {
  const body = content.replace(/^#[^\n]*\n+/, "");
  const lower = body.toLocaleLowerCase();
  const hits = terms.map((term) => lower.indexOf(term)).filter((i) => i >= 0);
  const at = hits.length ? Math.min(...hits) : 0;
  const start = Math.max(0, body.lastIndexOf("\n", Math.max(0, at - 80)) + 1);
  const excerpt = body.slice(start, start + 320).replace(/\s+/g, " ").trim();
  return start + 320 < body.length ? `${excerpt}…` : excerpt;
}

/**
 * Keyword search across titles, section names and page bodies. Title and
 * section hits outweigh body mentions so "uploads" finds the Uploads page
 * before every page that happens to mention uploading.
 */
export function searchHelpDocs(query: string, limit = 5): HelpDocMatch[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];

  return PUBLIC_HELP_DOCS
    .map((doc) => {
      const title = `${doc.title} ${doc.section}`.toLocaleLowerCase();
      const body = doc.content.toLocaleLowerCase();
      let score = 0;
      let matched = 0;
      for (const term of terms) {
        const inTitle = title.includes(term);
        const inBody = countOccurrences(body, term);
        if (inTitle || inBody) matched += 1;
        score += (inTitle ? 10 : 0) + Math.min(inBody, 8);
      }
      // Pages that cover more of the question's terms rank first.
      return { doc, score: score + matched * 5, matched };
    })
    .filter(({ matched }) => matched > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ doc }) => {
      const { content, ...rest } = doc;
      return { ...rest, excerpt: excerptFor(content, terms) };
    });
}
